import { CommonModule } from "@angular/common";
import { Component, computed, OnDestroy, OnInit, Optional, signal } from "@angular/core";
import { ImportWizardGeospatialFeatureCardComponent } from "./components/feature-card/import-wizard-geospatial-feature-card.component";
import { SelectComponent } from "@/app/shared/select/select.component";
import { ImportWizardGeoSpatialMiniMapComponent } from "../_components/minimap/import-wizard-geospatial-minimap.component";
import { MaterialModule } from "@/app/shared/modules/material.module";
import { ImportWizardStorage, ImportWizardStep } from "../../../import-wizard.storage";
import { MatDialogRef } from "@angular/material/dialog";
import { ImportWizardComponent } from "../../../import-wizard.component";
import { ImportWizardGeoSpatialValidationHandler } from "@/app/handlers/import-wizard/import-wizard-geospatial-validation.handler";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ImportWizardGeoSpatialService } from "../import-wizard-geospatial.service";
import { GetImportWizardLayerSummaryResponse, ImportWizardFile } from "@/app/dtos/import-wizard/import-wizard.dto";
import { SpinnerComponent } from "@/app/shared/spinner/spinner.component";
import { ImportWizardService } from "../../../import-wizard.service";
import { AlertComponent } from "@/app/shared/alert/alert.component";
import { SkeletonComponent } from "@/app/shared/skeleton/skeleton.component";

@Component({
    selector: 'app-import-wizard-geospatial-features-list',
    templateUrl: './import-wizard-geospatial-features-list.component.html',
    styleUrls: ['./import-wizard-geospatial-features-list.component.scss'],
    providers: [ImportWizardGeoSpatialValidationHandler],
    imports: [
        CommonModule,
        MaterialModule,
        SelectComponent,
        SpinnerComponent,
        AlertComponent,
        SkeletonComponent,
        ImportWizardGeospatialFeatureCardComponent,
        ImportWizardGeoSpatialMiniMapComponent
    ],
    standalone: true
})
export class ImportWizardGeospatialFeaturesListComponent implements OnInit, OnDestroy {

    _state = computed(() => this.importWizardStorage.state());

    importWizardFiles = signal<ImportWizardFile[]>([]);

    searchTerm = signal<string>('');

    filteredFiles = computed(() => {
        const term = this.normalize(this.searchTerm());
        if (!term) return this.importWizardFiles();
        return this.importWizardFiles().filter(file =>
            this.normalize(file.filename ?? '').includes(term)
        );
    });

    // LOADING
    loadingProgress = signal<number>(0);

    // FLAGS
    isLoading = signal<boolean>(false);
    isFinishing = signal<boolean>(false);

    isAlertVisible = false;

    actionsListOptions = [
        { value: 'complete', label: 'Concluir Importação' },
        { value: 'validate-and-complete', label: 'Validar e Concluir Importação' },
    ];

    constructor(
        public importWizardStorage: ImportWizardStorage,
        private importWizardGeoSpatialService: ImportWizardGeoSpatialService,
        private importWizardService: ImportWizardService,
        private validationHandler: ImportWizardGeoSpatialValidationHandler,
        @Optional() private dialogRef: MatDialogRef<ImportWizardComponent>
    ) {
        this.validationHandler.progress$
            .pipe(takeUntilDestroyed())
            .subscribe(progress => {
                this.loadingProgress.set(progress);
                if (progress === 100) this.isFinishing.set(false);
            });
    }

    ngOnInit(): void {
        this.validationHandler.init(this._state().id, 1, 100);
        this.getImportWizardData();

        // if (!this.importWizardStorage.hasFileInMemory()) {
        //     this.importWizardGeoSpatialService.useGetImportFile();
        // }
    }

    handleValidationClick() { 
        this.isFinishing.set(true);
        this.validationHandler.start({
            importId: this._state().id,
            columns: this.importWizardStorage.displayColumns(),
            page: 1,
            pageSize: 100
        });
    }

    handleCancelClick(): void {
        this.isAlertVisible = true;
    }

    handleCancelClickConfirmation(): void {
        this.isAlertVisible = false;
        this.importWizardStorage.handleCancelClickConfirmation(this.dialogRef);
    }

    handleCancelClickCancel(): void {
        this.isAlertVisible = false;
    }

    search(event: Event): void {
        const value = (event.target as HTMLInputElement).value;
        this.searchTerm.set(value);
    }

    handleDeletedImportWizardFile(deletedFile: ImportWizardFile) { 
        this.importWizardFiles.update(files => files.filter(file => file.id !== deletedFile.id));
    }

    private normalize(str: string): string {
        return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    }

    private getImportWizardData() {
        this.importWizardService.getImportWizard(this._state().id).subscribe({
            next: (data) => {
                this.importWizardFiles.set(data.files);
                this.importWizardStorage.setId(data.id);
                this.importWizardStorage.setFilename(data.dataset_name);
            },
            error: (error) => {
                console.error(error);
            }
        });
    }

    ngOnDestroy(): void {}
}